import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from './api/config'
import { apiClient } from './api/client'
import { AuthProvider, RequireAuth, useAuth } from './contexts/AuthContext'
import { ServiceContractGuard } from './features/service-contract/ServiceContractGuard'
import { RestaurantProvider, useRestaurant } from './contexts/RestaurantContext'
import { AppErrorBoundary } from './components/AppErrorBoundary'
import { RequireSuperAdmin } from './components/RequireRole'
import { Orders } from './pages/Orders'
import { usePermissions } from './hooks/usePermissions'
import { RestaurantIdCopy } from './components/RestaurantIdCopy'
import { NotificationProvider, NotificationBadge } from './features/notifications'
import { AutoPrinter } from './features/orders/components/AutoPrinter'
import { AppUpdateBanner } from './components/AppUpdateBanner'
import { deviceSetupPending } from './pages/deviceSetupStorage'
import { canPrint } from './features/orders/hooks/usePrintOrder'
import { ThemeProvider } from 'next-themes'
import { Toaster } from './components/ui/sonner'
import { cn } from './lib/utils'
// Lazy-loaded page components for code splitting
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })))
const MenuEditorV2 = lazy(() => import('./pages/MenuEditorV2').then(m => ({ default: m.MenuEditorV2 })))
const MenuPrintPage = lazy(() => import('./pages/MenuPrintPage').then(m => ({ default: m.MenuPrintPage })))
const VisualMenuEditorPage = lazy(() => import('./pages/VisualMenuEditorPage').then(m => ({ default: m.VisualMenuEditorPage })))
const AIMenuEditorPage = lazy(() => import('./pages/AIMenuEditorPage').then(m => ({ default: m.AIMenuEditorPage })))
const RestaurantSelector = lazy(() => import('./pages/RestaurantSelector').then(m => ({ default: m.RestaurantSelector })))
const DeploymentConsolePage = lazy(() => import('./pages/DeploymentConsolePage').then(m => ({ default: m.DeploymentConsolePage })))
const DiagnosticsPage = lazy(() => import('./pages/DiagnosticsPage').then(m => ({ default: m.DiagnosticsPage })))
const DeviceSetupPage = lazy(() => import('./pages/DeviceSetupPage').then(m => ({ default: m.DeviceSetupPage })))
const OpeningTimesEditor = lazy(() => import('./pages/OpeningTimesEditor'))
const OpeningHoursWebView = lazy(() => import('./pages/OpeningHoursWebView'))
const HolidaySettingsAdmin = lazy(() => import('./pages/HolidaySettingsAdmin'))
const AccountSecurity = lazy(() => import('./pages/AccountSecurity').then(m => ({ default: m.AccountSecurity })))
const DeliveryAreasEditor = lazy(() => import('./pages/DeliveryAreasEditor').then(m => ({ default: m.DeliveryAreasEditor })))
const GeneralSettings = lazy(() => import('./pages/GeneralSettings').then(m => ({ default: m.GeneralSettings })))
const PosSetupPage = lazy(() => import('./pages/PosSetupPage').then(m => ({ default: m.PosSetupPage })))
const Statistics = lazy(() => import('./pages/Statistics').then(m => ({ default: m.Statistics })))
const TaxReportPage = lazy(() => import('./pages/TaxReportPage').then(m => ({ default: m.TaxReportPage })))
const Vouchers = lazy(() => import('./pages/Vouchers').then(m => ({ default: m.Vouchers })))
const Announcements = lazy(() => import('./pages/Announcements').then(m => ({ default: m.Announcements })))
const LandingPagePage = lazy(() => import('./features/landing-page').then(m => ({ default: m.LandingPagePage })))
const OnboardingBoardPage = lazy(() => import('./features/onboarding').then(m => ({ default: m.OnboardingBoardPage })))
const OnboardingChecklistPage = lazy(() => import('./features/onboarding').then(m => ({ default: m.OnboardingChecklistPage })))
const ReservationsPage = lazy(() => import('./features/reservations').then(m => ({ default: m.ReservationsPage })))
import { Button } from './components/ui/button'
import { Input } from './components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover'
import { ScrollArea } from './components/ui/scroll-area'
import { LanguageSwitcher } from './components/LanguageSwitcher'
import { useDirection } from './hooks/useDirection'
import {
  Check,
  ChevronRight,
  Shield,
  ShoppingBag,
  BarChart3,
  UtensilsCrossed,
  Settings,
  ArrowRight,
  Menu,
  LogOut,
  Search,
  X,
  ChevronDown,
} from 'lucide-react'
import { NavigationDrawer, NavigationSidebar } from './components/NavigationDrawer'
import { Badge } from './components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card'
import { QuickStatsCards } from './features/dashboard/components/QuickStatsCards'
import { PendingOrdersBadge } from './features/dashboard/components/PendingOrdersBadge'
import { RecentActivityFeed } from './features/dashboard/components/RecentActivityFeed'
import { useDashboardData } from './features/dashboard/hooks/useDashboardData'
import type { RestaurantAdmin } from './features/restaurant-management'

function PageSpinner() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  )
}

// Full-screen wait for the restaurant to be auto-loaded; shows a retry UI when
// loading failed instead of spinning forever.
function RestaurantLoadingScreen() {
  const { restaurantLoadError, retryRestaurantLoad } = useRestaurant()
  const { t } = useTranslation('restaurants')

  if (!restaurantLoadError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-8">
      <h2 className="text-lg font-semibold mb-2">{t('loadError.title')}</h2>
      <p className="text-muted-foreground max-w-sm mb-5">{t('loadError.description')}</p>
      <Button variant="outline" onClick={retryRestaurantLoad}>
        {t('loadError.retry')}
      </Button>
    </div>
  )
}

// After a tab was hidden for a while (overnight tablet, backgrounded browser),
// refetch everything on return — SSE alone cannot be trusted to have survived.
const STALE_TAB_THRESHOLD_MS = 5 * 60 * 1000

function useRefreshStaleTab() {
  const queryClient = useQueryClient()
  const hiddenAtRef = useRef<number | null>(null)

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now()
      } else if (
        hiddenAtRef.current !== null &&
        Date.now() - hiddenAtRef.current > STALE_TAB_THRESHOLD_MS
      ) {
        hiddenAtRef.current = null
        void queryClient.invalidateQueries()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [queryClient])
}

function Dashboard() {
  const { selectedRestaurant, isRestaurantLocked } = useRestaurant()
  const { canSelectAnyRestaurant, canManageHolidayTemplates, canManageRestaurants, isSuperAdmin } = usePermissions()
  const { t } = useTranslation('restaurants')
  const { t: tDashboard } = useTranslation('dashboard')

  // Redirect to restaurant selector if no restaurant is selected (super_admin only)
  if (!selectedRestaurant && canSelectAnyRestaurant) {
    return <Navigate to="/restaurants" replace />
  }

  // Restaurant admins wait for their restaurant to be auto-loaded
  if (!selectedRestaurant) {
    return <RestaurantLoadingScreen />
  }

  return (
    <DashboardContent
      selectedRestaurant={selectedRestaurant}
      isRestaurantLocked={isRestaurantLocked}
      canSelectAnyRestaurant={canSelectAnyRestaurant}
      canManageHolidayTemplates={canManageHolidayTemplates}
      canManageRestaurants={canManageRestaurants}
      isSuperAdmin={isSuperAdmin}
      t={t}
      tDashboard={tDashboard}
    />
  )
}

// Landing page component that redirects to orders (simplified workflow)
function LandingRedirect() {
  const { selectedRestaurant } = useRestaurant()
  const { canSelectAnyRestaurant } = usePermissions()

  // Super admins without a restaurant selected go to restaurant selector
  if (!selectedRestaurant && canSelectAnyRestaurant) {
    return <Navigate to="/restaurants" replace />
  }

  // Restaurant admins wait for their restaurant to be auto-loaded
  if (!selectedRestaurant) {
    return <RestaurantLoadingScreen />
  }

  // Redirect to orders - the primary workflow for restaurant staff
  return <Navigate to="/orders" replace />
}

interface DashboardContentProps {
  selectedRestaurant: { id: number; name: string; iconPath?: string }
  isRestaurantLocked: boolean
  canSelectAnyRestaurant: boolean
  canManageHolidayTemplates: boolean
  canManageRestaurants: boolean
  isSuperAdmin: boolean
  t: (key: string) => string
  tDashboard: (key: string, options?: Record<string, unknown>) => string
}

function DashboardContent({
  selectedRestaurant,
  isRestaurantLocked,
  canSelectAnyRestaurant,
  canManageHolidayTemplates,
  canManageRestaurants,
  isSuperAdmin,
  t,
  tDashboard,
}: DashboardContentProps) {
  const { stats, recentActivity, isLoading } = useDashboardData({
    restaurantId: selectedRestaurant.id,
  })

  return (
    <div className="p-4 sm:p-8 space-y-6">
      {/* Restaurant Header */}
      <div className="flex items-center gap-4">
        <RestaurantAvatar
          name={selectedRestaurant.name}
          iconPath={selectedRestaurant.iconPath}
          size="lg"
        />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">{selectedRestaurant.name}</h1>
          {!isRestaurantLocked && canSelectAnyRestaurant && (
            <Link
              to="/restaurants"
              className="text-sm text-muted-foreground hover:text-primary"
            >
              {t('dashboard.changeRestaurant')}
            </Link>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <QuickStatsCards stats={stats} isLoading={isLoading} />

      {/* Pending Orders Alert */}
      {stats.pendingOrdersCount > 0 && (
        <PendingOrdersBadge count={stats.pendingOrdersCount} />
      )}

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <RecentActivityFeed activities={recentActivity} isLoading={isLoading} />
        </div>

        {/* Quick Actions */}
        <div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">
                {tDashboard('actions.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link
                to="/orders"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center">
                    <ShoppingBag className="h-4 w-4 text-blue-700" />
                  </div>
                  <span className="font-medium">{tDashboard('actions.viewOrders')}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
              <Link
                to="/statistics"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center">
                    <BarChart3 className="h-4 w-4 text-emerald-700" />
                  </div>
                  <span className="font-medium">{tDashboard('actions.viewStatistics')}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
              <Link
                to="/menu"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center">
                    <UtensilsCrossed className="h-4 w-4 text-purple-700" />
                  </div>
                  <span className="font-medium">{tDashboard('actions.editMenu')}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
              <Link
                to="/settings"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center">
                    <Settings className="h-4 w-4 text-amber-700" />
                  </div>
                  <span className="font-medium">{tDashboard('actions.manageSettings')}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            </CardContent>
          </Card>

          {/* Link to detailed statistics */}
          <Link
            to="/statistics"
            className="block mt-4 text-sm text-center text-muted-foreground hover:text-primary"
          >
            {tDashboard('links.detailedStats')} →
          </Link>
        </div>
      </div>

      {/* Feature Cards Section */}
      <div>
        <h2 className="text-lg font-semibold mb-4">{t('dashboard.menuEditor').split(' ')[0]} & More</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <Link
            to="/menu"
            className="block p-5 bg-card rounded-lg border hover:border-primary transition-colors"
          >
            <h3 className="font-semibold mb-1">{t('dashboard.menuEditor')}</h3>
            <p className="text-muted-foreground text-sm">
              {t('dashboard.menuEditorDesc')}
            </p>
          </Link>
          <Link
            to="/opening-times"
            className="block p-5 bg-card rounded-lg border hover:border-primary transition-colors"
          >
            <h3 className="font-semibold mb-1">{t('dashboard.openingTimes')}</h3>
            <p className="text-muted-foreground text-sm">
              {t('dashboard.openingTimesDesc')}
            </p>
          </Link>
          {isSuperAdmin && (
            <Link
              to="/delivery-areas"
              className="block p-5 bg-card rounded-lg border hover:border-primary transition-colors"
            >
              <h3 className="font-semibold mb-1">{t('dashboard.deliveryAreas')}</h3>
              <p className="text-muted-foreground text-sm">{t('dashboard.deliveryAreasDesc')}</p>
            </Link>
          )}
          <Link
            to="/settings/vouchers"
            className="block p-5 bg-card rounded-lg border hover:border-primary transition-colors"
          >
            <h3 className="font-semibold mb-1">{t('dashboard.vouchers')}</h3>
            <p className="text-muted-foreground text-sm">{t('dashboard.vouchersDesc')}</p>
          </Link>
          {canManageHolidayTemplates && (
            <Link
              to="/settings/holidays"
              className="block p-5 bg-card rounded-lg border hover:border-primary transition-colors"
            >
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">{t('dashboard.holidaySettings')}</h3>
                <Badge variant="secondary" className="text-xs">
                  <Shield className="w-3 h-3 mr-1" />
                  Admin
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm">{t('dashboard.holidaySettingsDesc')}</p>
            </Link>
          )}
          {canManageRestaurants && (
            <Link
              to="/restaurants"
              className="block p-5 bg-card rounded-lg border hover:border-primary transition-colors"
            >
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">{t('dashboard.restaurantManagement')}</h3>
                <Badge variant="secondary" className="text-xs">
                  <Shield className="w-3 h-3 mr-1" />
                  Admin
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm">{t('dashboard.restaurantManagementDesc')}</p>
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

function Navbar({
  onMenuClick,
  onDesktopNavToggle,
  isDesktopNavHidden = false,
}: {
  onMenuClick?: () => void
  onDesktopNavToggle?: () => void
  isDesktopNavHidden?: boolean
}) {
  const { logout } = useAuth()
  const { selectedRestaurant } = useRestaurant()
  const { canAccessLegacyAdmin, canSelectAnyRestaurant, isSuperAdmin } = usePermissions()
  const { t } = useTranslation()
  const location = useLocation()
  const isOrdersPage = location.pathname === '/orders'
  useDirection() // Apply RTL direction based on language

  return (
    <nav className="shrink-0 border-b border-border/60 overflow-hidden pt-[env(safe-area-inset-top)] z-30 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="px-2.5 sm:px-4 h-14 flex items-center gap-1.5 sm:gap-2.5 min-w-0">
        {/* Desktop sidebar toggle */}
        {onDesktopNavToggle && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onDesktopNavToggle}
            className="hidden lg:inline-flex h-9 w-9 text-muted-foreground hover:text-foreground"
            aria-label={isDesktopNavHidden ? 'Show navigation sidebar' : 'Hide navigation sidebar'}
            title={isDesktopNavHidden ? 'Show navigation sidebar' : 'Hide navigation sidebar'}
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Hamburger menu - only on mobile/tablet */}
        {onMenuClick && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="h-9 w-9 lg:hidden text-muted-foreground hover:text-foreground"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Brand - hidden on desktop since sidebar has it, hidden on small mobile when restaurant selected */}
        <Link to="/" className={cn('font-display font-bold text-lg lg:hidden truncate tracking-tight', selectedRestaurant ? 'hidden sm:block sm:max-w-none' : 'max-w-[120px] sm:max-w-none')}>
          {t('nav.brand')}
        </Link>

        {/* Role indicator for super admin */}
        {isSuperAdmin && (
          <Badge variant="outline" className="hidden sm:flex text-xs gap-1 text-muted-foreground border-border">
            <Shield className="w-3 h-3" />
            Admin
          </Badge>
        )}

        {/* Restaurant selection: super admins can always switch, also with nothing selected */}
        {(canSelectAnyRestaurant || !!selectedRestaurant) && (
          <>
            <ChevronRight className="h-4 w-4 text-muted-foreground/50 hidden sm:block rtl:rotate-180" />
            {canSelectAnyRestaurant ? (
              <RestaurantSwitcher />
            ) : selectedRestaurant && (
              <div className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2.5 py-1.5 min-w-0">
                <RestaurantAvatar
                  name={selectedRestaurant.name}
                  iconPath={selectedRestaurant.iconPath}
                  size="sm"
                />
                <span className="text-sm font-semibold max-w-[72px] sm:max-w-[150px] truncate">
                  {selectedRestaurant.name}
                </span>
              </div>
            )}
            {isSuperAdmin && selectedRestaurant && (
              <span className="hidden sm:inline-flex">
                <RestaurantIdCopy id={selectedRestaurant.id} />
              </span>
            )}
          </>
        )}

        <div className="ms-auto flex items-center gap-0.5 sm:gap-1 lg:gap-2 min-w-0 shrink-0">
          {/* Order notifications - hidden on /orders since orders are already visible */}
          {selectedRestaurant && !isOrdersPage && <NotificationBadge />}
          <LanguageSwitcher />
          {canAccessLegacyAdmin && (
            <a
              href={`${API_BASE_URL}/editrestaurants`}
              className="hidden lg:block text-sm text-muted-foreground hover:text-foreground px-1.5"
            >
              {t('nav.legacyAdmin')}
            </a>
          )}
          <div className="hidden sm:block h-5 w-px bg-border mx-0.5" />
          <Button variant="ghost" size="icon" onClick={logout} title={t('nav.logout')} className="h-9 w-9 text-muted-foreground hover:text-destructive">
            <LogOut className="h-4 w-4" />
            <span className="sr-only">{t('nav.logout')}</span>
          </Button>
        </div>
      </div>
    </nav>
  )
}

function RestaurantSwitcher() {
  const { selectedRestaurant, setSelectedRestaurant, clearSelectedRestaurant } = useRestaurant()
  const { t } = useTranslation('restaurants')
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const restaurantsQuery = useQuery({
    queryKey: ['admin-restaurants'],
    queryFn: async () => {
      const response = await apiClient.get<RestaurantAdmin[]>('/api/admin/restaurants')
      return response.data
    },
    enabled: open,
  })

  const restaurants = (restaurantsQuery.data ?? []).filter((restaurant) => !restaurant.isTemplate)
  const normalizedQuery = searchQuery.toLocaleLowerCase().trim()
  const filteredRestaurants = restaurants.filter((restaurant) => {
    if (!normalizedQuery) return true
    return (
      restaurant.name?.toLocaleLowerCase().includes(normalizedQuery) === true ||
      String(restaurant.id).includes(normalizedQuery)
    )
  })

  const selectRestaurant = (restaurant: RestaurantAdmin) => {
    setSelectedRestaurant({
      id: restaurant.id,
      name: restaurant.name ?? t('selector.restaurantFallback', { id: restaurant.id }),
      isTemplate: restaurant.isTemplate,
      iconPath: restaurant.iconPath ?? undefined,
      currency: restaurant.currency,
    })
    setOpen(false)
    setSearchQuery('')
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setSearchQuery('')
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2.5 py-1.5 rounded-lg hover:bg-accent/60 transition-colors min-w-0 border border-transparent hover:border-border/60 touch-manipulation"
          aria-label={t('dashboard.changeRestaurant')}
        >
          {selectedRestaurant ? (
            <>
              <RestaurantAvatar
                name={selectedRestaurant.name}
                iconPath={selectedRestaurant.iconPath}
                size="sm"
              />
              <span className="text-sm font-semibold max-w-[72px] sm:max-w-[150px] truncate">
                {selectedRestaurant.name}
              </span>
            </>
          ) : (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              {t('switcher.select')}
              <ChevronDown className="h-4 w-4" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={8}
        className="w-[calc(100vw-1rem)] sm:w-80 p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          searchInputRef.current?.focus()
        }}
      >
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={searchInputRef}
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              placeholder={t('selector.searchPlaceholder')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && filteredRestaurants[0]) {
                  event.preventDefault()
                  selectRestaurant(filteredRestaurants[0])
                }
              }}
              className="pl-9"
            />
          </div>
        </div>
        <ScrollArea className="h-[min(20rem,50dvh)]">
          <div className="p-1">
            {restaurantsQuery.isLoading ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('switcher.loading')}
              </p>
            ) : restaurantsQuery.isError ? (
              <button
                type="button"
                className="w-full px-3 py-6 text-center text-sm text-destructive"
                onClick={() => void restaurantsQuery.refetch()}
              >
                {t('switcher.loadError')}
              </button>
            ) : filteredRestaurants.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {searchQuery ? t('selector.noResultsSearch') : t('selector.noResults')}
              </p>
            ) : (
              filteredRestaurants.map((restaurant) => {
                const name =
                  restaurant.name ?? t('selector.restaurantFallback', { id: restaurant.id })
                const isSelected = restaurant.id === selectedRestaurant?.id

                return (
                  <button
                    key={restaurant.id}
                    type="button"
                    onClick={() => selectRestaurant(restaurant)}
                    className="w-full flex items-center gap-3 rounded-md px-2 py-2 text-start hover:bg-accent active:bg-accent touch-manipulation"
                  >
                    <RestaurantAvatar
                      name={name}
                      iconPath={restaurant.iconPath ?? undefined}
                      size="sm"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{name}</span>
                      <span className="block text-xs text-muted-foreground">#{restaurant.id}</span>
                    </span>
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  </button>
                )
              })
            )}
          </div>
        </ScrollArea>
        {selectedRestaurant && (
          <div className="border-t p-1">
            <button
              type="button"
              onClick={() => {
                clearSelectedRestaurant()
                setOpen(false)
                setSearchQuery('')
              }}
              className="w-full flex items-center gap-2 rounded-md px-2 py-2 text-start text-sm text-muted-foreground hover:bg-accent hover:text-foreground touch-manipulation"
            >
              <X className="h-4 w-4 shrink-0" />
              {t('switcher.clearSelection')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

function RestaurantAvatar({
  name,
  iconPath,
  size = 'md',
}: {
  name?: string
  iconPath?: string
  size?: 'sm' | 'md' | 'lg'
}) {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs rounded',
    md: 'w-10 h-10 text-sm rounded-lg',
    lg: 'w-14 h-14 text-xl rounded-lg',
  }

  if (iconPath) {
    return (
      <img
        src={`${API_BASE_URL}${iconPath}`}
        alt={name ?? 'Restaurant'}
        className={`${sizeClasses[size]} object-cover`}
      />
    )
  }

  // Letter avatar fallback
  const displayName = name ?? 'R'
  const letter = displayName.charAt(0).toUpperCase()
  const colorIndex = hashStringToNumber(displayName) % avatarColors.length
  const colors = avatarColors[colorIndex]

  return (
    <div className={`${sizeClasses[size]} flex items-center justify-center font-semibold ${colors}`}>
      {letter}
    </div>
  )
}

function hashStringToNumber(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash)
}

const avatarColors = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-purple-100 text-purple-700',
  'bg-orange-100 text-orange-700',
  'bg-pink-100 text-pink-700',
  'bg-cyan-100 text-cyan-700',
  'bg-amber-100 text-amber-700',
  'bg-indigo-100 text-indigo-700',
]

// Inner layout that has access to RestaurantContext
function ProtectedLayoutContent() {
  const [isNavOpen, setIsNavOpen] = useState(false)
  const openNav = useCallback(() => setIsNavOpen(true), [])
  const [isDesktopNavHidden, setIsDesktopNavHidden] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem('admin-v2.desktop-nav-hidden') === '1'
  })
  const { selectedRestaurant } = useRestaurant()
  const location = useLocation()
  useRefreshStaleTab()

  useEffect(() => {
    window.localStorage.setItem('admin-v2.desktop-nav-hidden', isDesktopNavHidden ? '1' : '0')
  }, [isDesktopNavHidden])

  // Content wrapped conditionally with NotificationProvider when restaurant is selected
  const content = (
    <div className="h-[100dvh] bg-background flex overflow-hidden">
      {/* Desktop: Persistent sidebar */}
      {!isDesktopNavHidden && (
        <NavigationSidebar className="hidden lg:flex fixed left-0 top-0 z-40" />
      )}

      {/* Mobile/Tablet: Navigation Drawer */}
      <NavigationDrawer isOpen={isNavOpen} onOpen={openNav} onClose={() => setIsNavOpen(false)} />

      {/* Main content area */}
      <div className={`flex-1 flex flex-col min-h-0 min-w-0 ${isDesktopNavHidden ? '' : 'lg:ml-[240px]'}`}>
        <Navbar
          onMenuClick={() => setIsNavOpen(true)}
          onDesktopNavToggle={() => setIsDesktopNavHidden((previous) => !previous)}
          isDesktopNavHidden={isDesktopNavHidden}
        />
        <main className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden pb-[env(safe-area-inset-bottom)]">
          <AppErrorBoundary resetKey={location.pathname}>
          <Suspense fallback={<PageSpinner />}>
            <Routes>
              <Route path="/" element={<LandingRedirect />} />
              <Route path="/dashboard" element={<RequireSuperAdmin><Dashboard /></RequireSuperAdmin>} />
              <Route path="/restaurants" element={<RestaurantSelector />} />
              <Route
                path="/deployment"
                element={
                  <RequireSuperAdmin>
                    <DeploymentConsolePage />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="/diagnostics"
                element={
                  <RequireSuperAdmin>
                    <DiagnosticsPage />
                  </RequireSuperAdmin>
                }
              />
              <Route path="/menu" element={<MenuEditorV2 key={selectedRestaurant?.id ?? "no-restaurant"} />} />
              <Route path="/print-menu" element={<RequireSuperAdmin><MenuPrintPage /></RequireSuperAdmin>} />
              <Route path="/visual-menu" element={<RequireSuperAdmin><VisualMenuEditorPage /></RequireSuperAdmin>} />
              <Route path="/ai-menu" element={<RequireSuperAdmin><AIMenuEditorPage /></RequireSuperAdmin>} />
              <Route path="/opening-times" element={<OpeningHoursWebView />} />
              <Route
                path="/admin/opening-times-editor"
                element={
                  <RequireSuperAdmin>
                    <OpeningTimesEditor />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="/admin/app-settings"
                element={
                  <RequireSuperAdmin>
                    <Navigate to="/settings?section=apps" replace />
                  </RequireSuperAdmin>
                }
              />
              <Route path="/orders" element={<Orders />} />
              <Route path="/setup" element={<DeviceSetupPage />} />
              <Route path="/pos-setup" element={<RequireSuperAdmin><PosSetupPage /></RequireSuperAdmin>} />
              <Route path="/reservations" element={<ReservationsPage />} />
              <Route path="/delivery-areas" element={<RequireSuperAdmin><DeliveryAreasEditor /></RequireSuperAdmin>} />
              <Route
                path="/settings"
                element={<GeneralSettings key={selectedRestaurant?.id ?? 'no-restaurant'} />}
              />
              <Route path="/settings/general" element={<Navigate to="/settings" replace />} />
              <Route path="/settings/vouchers" element={<Vouchers />} />
              <Route path="/account/security" element={<RequireSuperAdmin><AccountSecurity /></RequireSuperAdmin>} />
              <Route path="/announcements" element={<Announcements />} />
              <Route
                path="/landing-page"
                element={
                  <RequireSuperAdmin>
                    <LandingPagePage />
                  </RequireSuperAdmin>
                }
              />
              <Route path="/statistics" element={<RequireSuperAdmin><Statistics /></RequireSuperAdmin>} />
              <Route path="/tax-report" element={<TaxReportPage />} />
              <Route
                path="/onboarding"
                element={
                  <RequireSuperAdmin>
                    <OnboardingChecklistPage />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="/onboarding/board"
                element={
                  <RequireSuperAdmin>
                    <OnboardingBoardPage />
                  </RequireSuperAdmin>
                }
              />
              {/* Admin-only routes */}
              <Route
                path="/settings/holidays"
                element={
                  <RequireSuperAdmin>
                    <HolidaySettingsAdmin />
                  </RequireSuperAdmin>
                }
              />
            </Routes>
          </Suspense>
          </AppErrorBoundary>
        </main>
      </div>
      {/* Global toast notifications */}
      <Toaster position="top-center" />
    </div>
  )

  // Wrap with NotificationProvider only when a restaurant is selected
  // This enables order notifications on ALL pages (not just Orders)
  if (selectedRestaurant) {
    return (
      <NotificationProvider restaurantId={selectedRestaurant.id}>
        {canPrint && <AutoPrinter key={selectedRestaurant.id} restaurantId={selectedRestaurant.id} />}
        {canPrint && <AppUpdateBanner />}
        {content}
      </NotificationProvider>
    )
  }

  return content
}

function ProtectedLayout() {
  return (
    <RequireAuth>
      <RestaurantProvider>
        <ServiceContractGuard>
          <ProtectedLayoutContent />
        </ServiceContractGuard>
      </RestaurantProvider>
    </RequireAuth>
  )
}

// We set up new tablets ourselves, before anyone logs in or the restaurant signs its contract.
function DeviceSetupFirst({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState(deviceSetupPending)
  if (pending) return <DeviceSetupPage onDone={() => setPending(false)} />
  return <>{children}</>
}

function App() {
  return (
    <ThemeProvider attribute="class" themes={['light', 'pos-theme']} defaultTheme="light" storageKey="leckerorder-theme">
      <BrowserRouter>
        <AuthProvider>
          <AppErrorBoundary>
            <Suspense fallback={<PageSpinner />}>
              <DeviceSetupFirst>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/*" element={<ProtectedLayout />} />
                </Routes>
              </DeviceSetupFirst>
            </Suspense>
          </AppErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
